import {faVolumeMute, faVolumeUp} from '@fortawesome/free-solid-svg-icons'
import {Howl} from 'howler'
import * as R from 'ramda'
import * as React from 'react'
import {connect} from 'react-redux'
import type {TGetGameDataResponse} from '@kapitalist/contract/endpoints'
import {defaultRuleset} from '@kapitalist/contract/ruleset'
import type {TGameData, TGameSpectatorData} from '@kapitalist/contract/types'
import {values} from '@kapitalist/utils/Map'
import {createSafe} from '@kapitalist/utils/NEList'
import {safely} from '@kapitalist/utils/safely'
import * as S from '@kapitalist/utils/Set'
import api from '~/api/endpoints'
import DashboardLayout from '~/layouts/DashboardLayout'
import GameData from '~/models/game/GameData'
import {toCreateMajorActionData} from '~/models/game/toCreateMajorActionData'
import {toCreateMinorActionData} from '~/models/game/toCreateMinorActionData'
import {SpectatorMenuButton} from '~/pages/GameLobby/SpectatorMenuButton/stateless'
import GameLobbyUI from '~/pages/GameLobby/stateless'
import type {IGameLobbyPagePathPieces} from '~/pages/GameLobbyV2/types'
import NotFound from '~/pages/NotFound'
import {addPastGamePositionsForGame} from '~/redux/reducers/pastGamePositions'
import type {ReduxState} from '~/redux/reducers/root'
import {setSpectatorData, spectatorsDataForPlayer} from '~/redux/reducers/spectator'
import type {ISignedInRouteComponentProps} from '~/routing/Route/SignedIn'
import {isolateSignedInRouteComponentProps} from '~/routing/Route/SignedIn'
import {AlternateStateFontAwesomeIcon} from '~/utils/FontAwesome/HoverableFontAwesomeIcon'
import {setStatePromise} from '~/utils/React'
import type {TReactSetStateStateParameter} from '~/utils/types'
import type {TAttackDirection} from '~/utils/UserNotifications'
import {sendNotification} from '~/utils/UserNotifications'
import styles from './styles.module.css'

interface IReduxProps extends Pick<
  ReduxState,
  'settingsState' | 'spectatorState' | 'pastGamePositionsState'
> {}
interface IProps
  extends
    ISignedInRouteComponentProps<IGameLobbyPagePathPieces>,
    IReduxProps,
    TDispatch {}

interface IState {
  currentGameData: GameData | undefined
  soundsEnabled: boolean
  gameDataRequestsPending: number
}

class GameLobbyPage extends React.Component<IProps, IState> {
  state: IState = {
    currentGameData: undefined,
    soundsEnabled: false,
    gameDataRequestsPending: 0,
  }

  muteButtonSound = new Howl({
    src: ['/sounds/knob.mp3', '/sounds/knob.ogg'],
  })

  receiveNewGameDataResponse =
    (initialLoad: boolean) => (gameDataResponse: TGetGameDataResponse) => {
      this.receiveNewGameData(gameDataResponse.gameData)
      this.receiveNewSpectatorData(initialLoad)(gameDataResponse.spectatorRequests)
    }

  receiveNewGameData = (gameData: TGameData) => {
    const oldGameData = this.state.currentGameData
    const newMD5 = GameData.hash(gameData)

    if (newMD5 === oldGameData?.hash) {
      return
    }

    const gameDataModel = new GameData(
      gameData,
      this.props.authenticatedPlayer.id,
      this.props.settingsState.betaFeatures.gameLog,
      newMD5
    )

    // store pastGamePositions in redux since the backend doesn't send the old ones?
    this.props.addPastGamePositionsForGame({
      gameId: gameDataModel.gameId,
      currentTime: gameData.currentGamePosition.time,
      pastGamePositions: gameDataModel.pastGamePositions ?? [],
    })

    this.setState(
      {
        currentGameData: gameDataModel,
      },
      () => {
        // NB: this callback is all just for user notifications
        const newGameData = this.state.currentGameData
        if (!oldGameData || !newGameData) {
          return
        }
        const newGamePos = newGameData.playerGamePosition
        const oldGamePos = oldGameData.playerGamePosition

        if (oldGamePos?.isMyMajorTurn() !== newGamePos?.isMyMajorTurn()) {
          sendNotification(
            {
              type: 'turnChange',
              isMyTurn: newGamePos?.isMyMajorTurn() ?? false,
            },
            this.props.settingsState.notifications
          )
        } else if (
          oldGamePos?.inProgressMajorAction === undefined &&
          newGamePos?.inProgressMajorAction
        ) {
          // new attack
          const attackDirection: TAttackDirection =
            newGamePos.inProgressMajorAction.majorActor.id ===
            this.props.authenticatedPlayer.id
              ? 'fromMe'
              : newGamePos.inProgressMajorAction.pendingMinorActions.find(
                    pma =>
                      pma.minorActorPlayerId === this.props.authenticatedPlayer.id
                  )
                ? 'againstMe'
                : 'uninvolved'
          sendNotification(
            {
              type: 'attack',
              direction: attackDirection,
            },
            this.props.settingsState.notifications
          )
        }
      }
    )
  }

  receiveNewSpectatorData =
    (initialLoad: boolean) => (spectatorData: TGameSpectatorData[]) => {
      // update Redux
      this.props.setSpectatorData(spectatorData)

      if (initialLoad) {
        // no notification on first page load
        return
      }

      // previously denied requests are treated the same as nonexistent requests
      const oldSpectatorData = spectatorsDataForPlayer(
        this.props.authenticatedPlayer.id,
        this.props.spectatorState.spectatorData
      ).filter(sd => sd.status !== 'denied')
      const newSpectatorData = spectatorsDataForPlayer(
        this.props.authenticatedPlayer.id,
        spectatorData
      ).filter(sd => sd.status !== 'denied')

      const oldSpectatorsIds = new Set(
        spectatorsDataForPlayer(
          this.props.authenticatedPlayer.id,
          oldSpectatorData
        ).map(sd => sd.spectatorPlayer.playerId)
      )
      const newSpectatorsIds = new Set(
        spectatorsDataForPlayer(
          this.props.authenticatedPlayer.id,
          newSpectatorData
        ).map(sd => sd.spectatorPlayer.playerId)
      )

      if (!S.subset(newSpectatorsIds, oldSpectatorsIds)) {
        // new spectator
        sendNotification({type: 'spectator'}, this.props.settingsState.notifications)
      }
    }

  static requestsPendingStateChange =
    (delta: -1 | 1): TReactSetStateStateParameter<IState, any, any> =>
    prevState => ({
      gameDataRequestsPending: Math.max(
        0,
        prevState.gameDataRequestsPending + delta
      ),
    })

  render() {
    const rules = defaultRuleset
    const {settingsState} = this.props
    const {gameLobbySize, skin} = settingsState
    const maybeGameId = this.props.match.params.maybeGameId

    return (
      <DashboardLayout
        {...isolateSignedInRouteComponentProps(this.props)}
        topRightStuff={
          <>
            {this.state.currentGameData?.playerGamePosition && (
              <SpectatorMenuButton
                className={styles.spectatorIconWrapper}
                spectatorData={this.props.spectatorState.spectatorData}
                playerGamePosition={this.state.currentGameData.playerGamePosition}
                approveSpectator={spectatorPlayerId => {
                  // oxlint-disable-next-line @typescript/no-non-null-assertion
                  const gameId = this.state.currentGameData!.gameId

                  return setStatePromise(
                    this,
                    GameLobbyPage.requestsPendingStateChange(1)
                  )
                    .then(() =>
                      api.inGame.approveSpectatorRequest.execute(
                        {},
                        {spectatorPlayerId, gameId}
                      )
                    )
                    .then(this.receiveNewGameDataResponse(false))
                    .finally(() =>
                      this.setState(GameLobbyPage.requestsPendingStateChange(-1))
                    )
                }}
                revokeSpectator={spectatorPlayerId => {
                  // oxlint-disable-next-line @typescript/no-non-null-assertion
                  const gameId = this.state.currentGameData!.gameId

                  return setStatePromise(
                    this,
                    GameLobbyPage.requestsPendingStateChange(1)
                  )
                    .then(() =>
                      api.inGame.revokeSpectatorPermission.execute(
                        {},
                        {spectatorPlayerId, gameId}
                      )
                    )
                    .then(this.receiveNewGameDataResponse(false))
                    .finally(() =>
                      this.setState(GameLobbyPage.requestsPendingStateChange(-1))
                    )
                }}
              />
            )}
            <div
              className={styles.soundIconWrapper}
              onClick={() => {
                this.setState(prevState => ({
                  soundsEnabled: !prevState.soundsEnabled,
                }))
                // Chrome (et. al) have rules about playing sounds when there hasn't been any user interaction
                // the common wisdom seems to be, have a sound play in a click handler, then other sounds can play
                // freely for a long while.
                if (!this.state.soundsEnabled) {
                  this.muteButtonSound.play()
                }
              }}
            >
              <AlternateStateFontAwesomeIcon
                icon={{
                  icon: faVolumeMute,
                  style: {
                    width: '30px',
                    height: '30px',
                  },
                  color: 'grey-dark',
                }}
                alternateIcon={{
                  icon: faVolumeUp,
                  color: 'blue-light',
                }}
                alternateState={this.state.soundsEnabled}
              />
            </div>
          </>
        }
      >
        <api.preGame.getGameData.PromiseManager
          params={{}}
          pathPieces={{maybeGameId}}
          onSuccess={this.receiveNewGameDataResponse(true)}
          then={() => {
            const {currentGameData} = this.state
            if (!currentGameData) {
              return <NotFound />
            }
            const {gameId} = currentGameData

            return (
              <>
                <api.preGame.getGameData.PromisePoller
                  params={{}}
                  pathPieces={{maybeGameId: gameId}}
                  onSuccess={this.receiveNewGameDataResponse(false)}
                  frequencyMs={[3500, 1000, 3000, 5000, -1]}
                  pollImmediatelyOnMount={false}
                  pause={this.state.gameDataRequestsPending > 0}
                  restartKey={this.state.currentGameData?.currentGamePosition.time}
                />
                <GameLobbyUI
                  gameData={currentGameData}
                  allPastGamePositions={createSafe(
                    R.sortWith(
                      [R.ascend(a => a.date), R.ascend(a => a.gamePosition.time)],
                      values(
                        safely(this.state.currentGameData?.gameId, gid =>
                          this.props.pastGamePositionsState.games.get(gid)
                        )?.pastGamePositionsMap ?? new Map()
                      )
                    )
                  )}
                  skin={skin}
                  gameLobbySize={gameLobbySize}
                  rules={rules}
                  chooseMajorAction={majorAction =>
                    setStatePromise(
                      this,
                      GameLobbyPage.requestsPendingStateChange(1)
                    ).then(() =>
                      api.inGame.submitMajorAction
                        .execute(
                          {
                            time: currentGameData.currentGamePosition.time,
                            action: toCreateMajorActionData(majorAction),
                          },
                          {gameId}
                        )
                        .finally(() =>
                          this.setState(GameLobbyPage.requestsPendingStateChange(-1))
                        )
                    )
                  }
                  chooseMajorActionSubmitSuccess={r =>
                    this.receiveNewGameData(r.gameData)
                  }
                  chooseMinorAction={minorAction =>
                    setStatePromise(
                      this,
                      GameLobbyPage.requestsPendingStateChange(1)
                    ).then(() =>
                      api.inGame.submitMinorAction
                        .execute(
                          {
                            time: currentGameData.currentGamePosition.time,
                            action: toCreateMinorActionData(minorAction),
                          },
                          {gameId}
                        )
                        .finally(() =>
                          this.setState(GameLobbyPage.requestsPendingStateChange(-1))
                        )
                    )
                  }
                  chooseMinorActionSubmitSuccess={r =>
                    this.receiveNewGameData(r.gameData)
                  }
                />
              </>
            )
          }}
        />
      </DashboardLayout>
    )
  }
}

const mapStateToProps = (state: ReduxState): IReduxProps => ({
  settingsState: state.settingsState,
  spectatorState: state.spectatorState,
  pastGamePositionsState: state.pastGamePositionsState,
})

const mapDispatchToProps = {setSpectatorData, addPastGamePositionsForGame}
type TDispatch = typeof mapDispatchToProps

export default connect(mapStateToProps, mapDispatchToProps)(GameLobbyPage)
